import React from "react";

import { getUser, useAuthState } from "@synthesis/shared-auth";

import { uriPrefix } from "../../shared/api/axios-instance";

export const RUNNER_JOB_STATE_UPDATE_EVENT = "runner.processing.service.job.state.update";

export type WSNotificationPayload = {
  eventType?: string;
  sourceId?: string;
  sourceType?: string;
  serviceSource?: string;
  params?: Record<string, string> | null;
};

export type WSNotificationParams = {
  modelId?: string;
  projectId?: string;
  state?: string;
  outputFormat?: string;
  statusMessage?: string;
  retryCount?: string;
};

export type WSNotificationMessage = {
  id?: string;
  isDeleted?: boolean;
  isNew?: boolean;
  name?: string;
  type?: string;
  eventType?: string;
  sourceId?: string;
  sourceType?: string;
  sourceTitle?: string;
  params?: WSNotificationParams | null;
  targetUsers?: string[];
  timestamp?: string;
  payload?: WSNotificationPayload | null;
};

export type WSNotificationsState = {
  isAuthorized: boolean;
  clientWebSocket: WebSocket | null;
};

const DEFAULT_WS_STATE_VALUE: WSNotificationsState = { isAuthorized: false, clientWebSocket: null };

const WSNotificationsContext = React.createContext<WSNotificationsState>(DEFAULT_WS_STATE_VALUE);

export const WSNotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const authStore = useAuthState();
  const userId = authStore.currentUser?.id;

  const [wsState, setWSState] = React.useState<WSNotificationsState>(DEFAULT_WS_STATE_VALUE);

  React.useLayoutEffect(() => {
    if (!uriPrefix || !userId) {
      return;
    }

    const wsUrl = `${uriPrefix}/api/notification/notification`;

    const ws = new WebSocket(wsUrl);

    const handleOpen = () => {
      const auth = { type: "TOKEN", payload: { token: `Bearer ${getUser()?.access_token}`, userId } };

      ws.send(JSON.stringify(auth));
      setWSState((prev) => ({ ...prev, isAuthorized: ws.readyState === WebSocket.OPEN }));
    };

    ws.addEventListener("open", handleOpen);

    setWSState((prev) => ({ ...prev, clientWebSocket: ws }));

    return () => {
      ws.removeEventListener("open", handleOpen);
      ws.close();
      setWSState(DEFAULT_WS_STATE_VALUE);
    };
  }, [userId]);

  return <WSNotificationsContext.Provider value={wsState}>{children}</WSNotificationsContext.Provider>;
};

export const useWSNotifications = () => React.useContext<WSNotificationsState>(WSNotificationsContext);
